/**
 * Client Overpass — EF-POI-009, ENF-016, ENF-017.
 *
 * Chaque requête est émise en `POST` avec `Content-Type:
 * application/x-www-form-urlencoded`. Ce type appartient aux types dits sûrs de
 * la spécification Fetch : la requête n'est donc pas précédée d'une requête
 * préalable `OPTIONS`, ce qui épargne une trentaine d'allers-retours inutiles
 * sur un parcours de 1 500 km.
 *
 * L'en-tête `User-Agent` fait partie des en-têtes interdits et ne peut pas être
 * défini depuis un navigateur ; aucune tentative en ce sens n'est faite.
 * L'identification repose sur `Referer`, émis par le navigateur, et sur la
 * politique de provenance `strict-origin-when-cross-origin`.
 */

import { parseOverpassResponse, type OverpassResponse } from './response';

/** Délai d'attente client, égal à la déclaration `[timeout:60]` de la requête. */
export const REQUEST_TIMEOUT_MS = 60_000;

/** Intervalle minimal entre deux requêtes, en millisecondes (ENF-017). */
export const MIN_REQUEST_INTERVAL_MS = 1000;

/** Attente par défaut sur refus temporaire, en secondes. */
export const DEFAULT_RETRY_AFTER_SECONDS = 30;

/** Nombre total d'attentes sur refus temporaire pour l'ensemble d'une recherche. */
export const MAX_TEMPORARY_WAITS = 3;

/** Nombre d'essais automatiques supplémentaires par tronçon. */
export const MAX_AUTOMATIC_RETRIES = 1;

export type ChunkFailureKind = 'network' | 'timeout' | 'server' | 'temporary' | 'truncated' | 'client';

export type ChunkResult =
  | { readonly kind: 'ok'; readonly response: OverpassResponse }
  | {
      readonly kind: 'failed';
      readonly failure: ChunkFailureKind;
      readonly cause: string;
      /** Attente imposée par l'en-tête `Retry-After`, en secondes. */
      readonly retryAfterSeconds: number | null;
    };

export interface FetchLike {
  (input: string, init: RequestInit): Promise<Response>;
}

export interface OverpassClientOptions {
  readonly fetch?: FetchLike;
  readonly timeoutMs?: number;
  readonly minIntervalMs?: number;
  readonly now?: () => number;
  readonly sleep?: (milliseconds: number) => Promise<void>;
}

const defaultSleep = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

/**
 * Émet les requêtes une par une. Aucune requête n'est émise en dehors d'une
 * recherche explicitement lancée par l'utilisateur (CA-025).
 */
export class OverpassClient {
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;
  private readonly minIntervalMs: number;
  private readonly now: () => number;
  private readonly sleep: (milliseconds: number) => Promise<void>;
  private lastRequestAt = Number.NEGATIVE_INFINITY;
  private inFlight = false;

  constructor(options: OverpassClientOptions = {}) {
    this.fetchImpl = options.fetch ?? ((input, init): Promise<Response> => globalThis.fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
    this.minIntervalMs = options.minIntervalMs ?? MIN_REQUEST_INTERVAL_MS;
    this.now = options.now ?? ((): number => Date.now());
    this.sleep = options.sleep ?? defaultSleep;
  }

  async request(url: string, query: string, signal?: AbortSignal): Promise<ChunkResult> {
    if (this.inFlight) {
      throw new Error('Une requête Overpass est déjà en vol : ENF-017 interdit la simultanéité.');
    }
    this.inFlight = true;
    try {
      await this.respectInterval();
      return await this.send(url, query, signal);
    } finally {
      this.inFlight = false;
    }
  }

  private async respectInterval(): Promise<void> {
    const waited = this.now() - this.lastRequestAt;
    if (waited < this.minIntervalMs) await this.sleep(this.minIntervalMs - waited);
  }

  private async send(url: string, query: string, signal?: AbortSignal): Promise<ChunkResult> {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, this.timeoutMs);
    const onAbort = (): void => {
      controller.abort();
    };
    signal?.addEventListener('abort', onAbort);

    this.lastRequestAt = this.now();

    try {
      const response = await this.fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(query)}`,
        // Transmet la seule origine HTTPS de l'application, sans chemin ni
        // paramètre. `no-referrer` est interdit : il rendrait l'application
        // anonyme vis-à-vis de l'instance.
        referrerPolicy: 'strict-origin-when-cross-origin',
        signal: controller.signal,
        cache: 'no-store',
        mode: 'cors',
        credentials: 'omit',
      });

      // Le code de statut fait seule foi : le corps d'une réponse en échec
      // n'est jamais analysé pour en déduire la cause.
      if (!response.ok) return failureFromStatus(response);

      const payload: unknown = await response.json();
      const parsed = parseOverpassResponse(payload);
      if (parsed.remark !== null) {
        return {
          kind: 'failed',
          failure: 'truncated',
          cause: `Réponse incomplète signalée par le serveur : ${parsed.remark}`,
          retryAfterSeconds: null,
        };
      }
      return { kind: 'ok', response: parsed };
    } catch (error) {
      if (signal?.aborted === true) {
        return { kind: 'failed', failure: 'network', cause: 'Recherche annulée.', retryAfterSeconds: null };
      }
      if (controller.signal.aborted) {
        return {
          kind: 'failed',
          failure: 'timeout',
          cause: "Délai d'attente de 60 secondes dépassé.",
          retryAfterSeconds: null,
        };
      }
      return {
        kind: 'failed',
        failure: 'network',
        cause: describeNetworkFailure(error),
        retryAfterSeconds: null,
      };
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
  }
}

function failureFromStatus(response: Response): ChunkResult {
  const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
  if (response.status === 429 || response.status === 503 || response.status === 504) {
    return {
      kind: 'failed',
      failure: 'temporary',
      cause: `Refus temporaire du service (code ${response.status}).`,
      retryAfterSeconds: retryAfter,
    };
  }
  if (response.status >= 500) {
    return {
      kind: 'failed',
      failure: 'server',
      cause: `Erreur du serveur Overpass (code ${response.status}).`,
      retryAfterSeconds: retryAfter,
    };
  }
  return {
    kind: 'failed',
    failure: 'client',
    cause: `Requête refusée par le serveur Overpass (code ${response.status}).`,
    retryAfterSeconds: retryAfter,
  };
}

export function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const seconds = Number.parseInt(header.trim(), 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(header);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

/**
 * Une requête bloquée par CORS ou par une politique du navigateur est traitée
 * comme une erreur réseau, sans recourir à un proxy de contournement.
 */
function describeNetworkFailure(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/cors|cross-origin|blocked/i.test(message)) {
    return "Requête bloquée par la politique d'origine du navigateur.";
  }
  return `Erreur réseau : ${message}`;
}
